import { cartSnapshots, beginCartMutation, waitForCartMutations, isCartMutating, isCartRecovering, beginCartReadiness, blockCartCheckout } from './cart-state.js';
import { DISCOUNT_ORIGIN_ATTRIBUTE, discountOriginAttributes, resolveDiscountOrigin } from '@theme/discount-origin';
import { getAmerica250CartConfig } from '@theme/america-250-cart-config';
import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { isDiscountQuoteStale, discountStateKey, lookupDiscountMetadata } from './discount-pricing.js';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { ThemeEvents } from '@theme/events';
import {
  buildDiscountCodeList,
  clearFreeProcessingDiscount,
  getPrimaryDiscountCodeFromUrl,
  isAdditiveFreeProcessingDiscountCode,
  reconcileFreeProcessingDiscount,
} from '@theme/free-processing-discount';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Promise<boolean> | null} */
  #defaultDiscountSyncPromise = null;

  /** @type {string | null} */
  #defaultDiscountSyncCode = null;

  #connectionController = new AbortController();
  #connectionGeneration = 0;
  #pendingUpdates = 0;
  #discountOperationRevision = 0;
  #pageHidden = false;
  #cartDiscountCodes = null;
  #debouncedOnCartUpdate = debounce(this.#setDiscounts.bind(this), 500);

  get isUpdatingDiscount() {
    return this.#pendingUpdates > 0 || Boolean(this.#activeFetch);
  }

  async connectedCallback() {
    super.connectedCallback();
    this.#pageHidden = false;
    window.addEventListener('pagehide', this.#handlePageHide);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('cart:recovered', this.#handleCartRecovered);
    await this.#initializeDiscounts();
  }

  async #initializeDiscounts() {
    const recoveredCart = isCartRecovering() ? Theme.legacyBox.cart : null;
    const operationRevision = this.#discountOperationRevision;
    // Queued manual edits must see this confirmed rollback baseline before they resume.
    if (recoveredCart) this.#adoptCartSnapshot(recoveredCart);
    if (this.#connectionController.signal.aborted) this.#connectionController = new AbortController();
    const endReadiness = beginCartReadiness();
    const generation = ++this.#connectionGeneration;
    this.#pendingUpdates = 1;
    const isCurrent = () => this.isConnected && generation === this.#connectionGeneration;

    try {
      while (isCartMutating()) await waitForCartMutations();
      if (!isCurrent()) return;
      if (recoveredCart) {
        // Recovery adopts the server's decision; it must not retry a failed promotion write.
        if (operationRevision !== this.#discountOperationRevision) return;
        const code = this.#getPrimaryDiscountCode(this.#existingDiscounts());
        const config = Theme.legacyBox.discounts;
        const stored = this.#readSession(config.sessionKey);
        if (code) {
          const origin = resolveDiscountOrigin(code, stored, this.dataset.discountOrigin, config.autoApply.previousCodes);
          if (this.#normalizeDiscountCode(stored?.code) !== code || isDiscountQuoteStale(stored)) {
            await this.#persistPrimaryDiscount(code, origin || 'locally_applied');
          }
        } else if (recoveredCart.item_count > 0) {
          sessionStorage.removeItem(config.sessionKey);
          const fallback = this.#readSession(config.defaultSessionKey);
          // An ineligible Starter cart must not erase confirmed prices for eligible kits.
          if (!config.autoApply.active || isDiscountQuoteStale(fallback) ||
              this.#normalizeDiscountCode(fallback?.code) !== this.#normalizeDiscountCode(config.autoApply.code)) {
            this.#clearAutoApplyDefaultDiscount();
          }
        }
        if (isCurrent()) this.#notifyDisplayChange();
        return;
      }
      await this.#setDiscounts(null, true);
      if (!isCurrent()) return;
      await this.#autoDiscounts();
      if (!isCurrent()) return;

      const storedDiscount = this.#readSession(Theme.legacyBox.discounts.sessionKey);
      const shouldSyncDefault =
        !storedDiscount || ['auto_apply', 'override_applied'].includes(storedDiscount.applied);

      if (shouldSyncDefault) {
        const fallbackCode = Theme.legacyBox.discounts.autoApply.originalCode || Theme.legacyBox.discounts.autoApply.code;
        const autoApplyCode = await this.#resolveAutoApplyCode(fallbackCode, !storedDiscount);

        if (!isCurrent()) return;
        if (await this.#syncAutoApplyDefaultDiscount(autoApplyCode) && isCurrent()) {
          this.#notifyDisplayChange();
        }
      } else {
        this.#clearAutoApplyDefaultDiscount();
      }
    } catch (error) {
      console.error(error);
      if (isCurrent() && !this.#pageHidden && error.name !== 'AbortError') blockCartCheckout();
    } finally {
      endReadiness();
      if (generation === this.#connectionGeneration) this.#pendingUpdates--;
      if (!isCurrent()) return;
      document.body.classList.remove('discounts-loading');
      document.addEventListener(ThemeEvents.cartUpdate, this.#debouncedOnCartUpdate);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('pagehide', this.#handlePageHide);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('cart:recovered', this.#handleCartRecovered);
    this.#invalidateConnection();
    super.disconnectedCallback();
  }

  #handlePageHide = () => {
    this.#pageHidden = true;
    cartSnapshots.invalidate();
    this.#invalidateConnection();
  };

  #handleCartRecovered = () => { void this.#initializeDiscounts(); };

  #handlePageShow = (event) => {
    if (!event.persisted || !this.isConnected) return;
    this.#pageHidden = false;
    void this.#initializeDiscounts();
  };

  #invalidateConnection() {
    this.#connectionGeneration++;
    this.#pendingUpdates = 0;
    this.#debouncedOnCartUpdate.cancel();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#debouncedOnCartUpdate);
    this.#connectionController.abort();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#defaultDiscountSyncPromise = null;
    this.#defaultDiscountSyncCode = null;
    this.closest('.cart-discount')?.classList.remove('is-loading');
  }

  /**
   * @param {Event | null} event
   * @param {Boolean | null} justInitialized
   */
  async #setDiscounts(event, justInitialized) {
    if (event?.detail?.data?.source === 'cart-recovery') return;
    const generation = this.#connectionGeneration;
    this.#pendingUpdates++;
    try {
      await this.#reconcileDiscounts(event, justInitialized);
    } catch (error) {
      if (justInitialized) throw error;
      console.error(error);
      // A failed background read cannot make a previously confirmed cart uncertain.
      // Mutation failures are guarded in applyDiscount/removeDiscount themselves.
    } finally {
      if (generation === this.#connectionGeneration) this.#pendingUpdates--;
    }
  }

  /**
   * @param {Event | null} event
   * @param {Boolean | null} justInitialized
   */
  async #reconcileDiscounts(event, justInitialized) {
    if (!this.isConnected || this.#pageHidden || this.#activeFetch || isCartMutating()) return;
    const generation = this.#connectionGeneration;
    const rawStorage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);
    const revision = this.#discountOperationRevision;
    // History can restore markup from before another page changed the cart.
    // Background reconciliation must never write a code inferred from those pills.
    let cart;
    try { cart = await cartSnapshots.read(); }
    catch (error) {
      if (error.name === 'AbortError' || !this.isConnected || this.#pageHidden || isCartMutating()) throw error;
      cart = await cartSnapshots.read();
    }
    if (!this.isConnected || this.#pageHidden || this.#activeFetch || generation !== this.#connectionGeneration ||
        revision !== this.#discountOperationRevision ||
        sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) !== rawStorage) return;
    const displayState = value => JSON.stringify([value?.item_count, value?.total_price, value?.discount_codes,
      value?.cart_level_discount_applications?.map(discount => [discount.title, discount.total_allocated_amount]),
      value?.items?.map(item => [item.key || item.id, item.quantity, item.final_line_price, item.properties,
        item.discounts?.map(discount => [discount.title, discount.amount])])]);
    const restoredCartChanged = justInitialized && displayState(Theme.legacyBox.cart) !== displayState(cart);
    this.#adoptCartSnapshot(cart);
    // Existing cart surfaces fetch fresh section HTML for a real cart change.
    // Metadata-only notifications intentionally do not redraw them.
    if (restoredCartChanged) document.dispatchEvent(new DiscountUpdateEvent(cart, this.id));
    const existingDiscounts = this.#existingDiscounts();
    const storedDiscount = this.#readSession(Theme.legacyBox.discounts.sessionKey);
    const storage = storedDiscount ? rawStorage : null;
    const discountsData = storedDiscount || {};
    const storedCode = this.#normalizeDiscountCode(discountsData.code);

    const isOverrideDiscountApplied = Theme.legacyBox.discounts.overrides.find(
      (o) => !o.additive && !o.campaign_display && this.#normalizeDiscountCode(o.auto_discount) === storedCode
    );
    const override = Theme.legacyBox.discounts.overrides.find(
      (o) =>
        !o.campaign_display &&
        this.#isOverrideDiscountActive(o, discountsData) &&
        Theme.legacyBox.cart.items.find((i) => o.variant_id.includes(`${i.id}`))
    );
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = this.#getUrlDiscountValue();

    if (urlCode) {
      const type = urlParams.get('lb_discount_type') || 'url_query';
      const normalizedUrlCode = this.#normalizeDiscountCode(urlCode);
      const requestedDiscounts = buildDiscountCodeList(urlCode);
      const normalizedExistingDiscounts = existingDiscounts
        .map((code) => this.#normalizeDiscountCode(code))
        .filter(Boolean);
      const isUrlDiscountApplied = requestedDiscounts.every((code) =>
        normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(code))
      );

      let requestHandled = true;
      if (!isUrlDiscountApplied) {
        requestHandled = await this.applyDiscount(null, urlCode, type);
      } else if (storedCode !== normalizedUrlCode || discountsData.applied !== type || isDiscountQuoteStale(discountsData) ||
        this.dataset.discountOrigin !== discountOriginAttributes(urlCode, type)[DISCOUNT_ORIGIN_ATTRIBUTE]) {
        requestHandled = await this.applyDiscount(null, urlCode, type);
        if (requestHandled) this.#notifyDisplayChange();
      }

      if (requestHandled) {
        url.searchParams.delete('lb_discount');
        url.searchParams.delete('lb_discount_type');
        url.searchParams.delete('discount');
        window.history.replaceState({}, '', url);
      }

      return;
    }

    const existingPrimaryDiscount = this.#getPrimaryDiscountCode(existingDiscounts);
    const restoredOrigin = resolveDiscountOrigin(existingPrimaryDiscount || storedCode, discountsData,
      this.dataset.discountOrigin, Theme.legacyBox.discounts.autoApply.previousCodes);
    // Update only an obsolete automatic selection, before old-code restoration
    // or product-override transitions can consume this update.
    if (restoredOrigin === 'auto_apply') {
      const autoApplyCode = await this.#resolveAutoApplyCode(
        Theme.legacyBox.discounts.autoApply.originalCode || Theme.legacyBox.discounts.autoApply.code
      );
      if (!this.isConnected || this.#activeFetch || generation !== this.#connectionGeneration ||
          sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) !== rawStorage) return;
      if (autoApplyCode && this.#normalizeDiscountCode(autoApplyCode) !== (existingPrimaryDiscount || storedCode)) {
        await this.applyDiscount(null, autoApplyCode, 'auto_apply');
        return;
      }
    }
    const storedPrimaryCode =
      storedCode && !isAdditiveFreeProcessingDiscountCode(storedCode) ? storedCode : null;
    const primaryCode = existingPrimaryDiscount || storedPrimaryCode;
    const expectedDiscounts = buildDiscountCodeList(primaryCode);
    const normalizedExistingDiscounts = existingDiscounts
      .map((code) => this.#normalizeDiscountCode(code))
      .filter(Boolean);
    const hasMissingDiscount = expectedDiscounts.some(
      (code) => !normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(code))
    );

    if (hasMissingDiscount && (primaryCode || !Theme.legacyBox.discounts.autoApply.active)) {
      const requestedCode = primaryCode || expectedDiscounts[0];
      const primaryType = !primaryCode
        ? 'campaign'
        : existingPrimaryDiscount && existingPrimaryDiscount !== storedCode
          ? restoredOrigin
          : restoredOrigin || discountsData.applied || 'locally_applied';
      await this.applyDiscount(null, requestedCode, primaryType);
      return;
    }

    if (existingPrimaryDiscount && storedCode && existingPrimaryDiscount !== storedCode) {
      await this.#persistPrimaryDiscount(existingPrimaryDiscount, restoredOrigin || 'locally_applied');
      this.#notifyDisplayChange();
      return;
    }

    // Direct-checkout offers apply their code only after their CTA adds the bundle.
    // A matching variant already in the cart is not consent to change that purchase.
    const isProductFormPurchase = event?.detail?.data?.source === 'product-form-component';
    const discountScope = isProductFormPurchase && event.target instanceof Element ? event.target : document;
    if (
      override &&
      !getAmerica250CartConfig(discountScope)?.redirectToCheckout &&
      (isProductFormPurchase || justInitialized) &&
      restoredOrigin !== 'groupon'
    ) {
      document.body.classList.add('discounts-override-applying');
      await this.applyDiscount(null, override.auto_discount, 'override_applied');
      document.body.classList.remove('discounts-override-applying');
      return;
    }

    // If product discount override discount is applied and no override product in the cart,
    // then apply auto discount or remove discount at all.
    if (!override && isOverrideDiscountApplied && Theme.legacyBox.cart.item_count !== 0) {
      document.body.classList.add('discounts-override-applying');

      const autoApplyCode = await this.#resolveAutoApplyCode(
        Theme.legacyBox.discounts.autoApply.code
      );

      if (autoApplyCode) {
        await this.applyDiscount(null, autoApplyCode, 'auto_apply');
      } else if (existingDiscounts[0]) {
        await this.removeDiscount(null, existingDiscounts[0]);
      }

      document.body.classList.remove('discounts-override-applying');
      return;
    }

    // If no any discounts applied and there is saved data in the storage,
    // then clear storage.
    if (!existingDiscounts.length && storage) {
      sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
      this.#notifyDisplayChange();
      return;
    }

    // If no saved data in the storage and there is applied discount and any item in the cart exists,
    // then get applied discount data.
    if (!storage && existingPrimaryDiscount && Theme.legacyBox.cart.item_count !== 0) {
      await this.#persistPrimaryDiscount(existingPrimaryDiscount, restoredOrigin || 'locally_applied');
      this.#notifyDisplayChange();
    }

    // Adopt known automatic/explicit provenance once, without recurring cart writes.
    if (!this.#activeFetch && this.isConnected && generation === this.#connectionGeneration &&
        this.#normalizeDiscountCode(this.#readSession(Theme.legacyBox.discounts.sessionKey)?.code) === existingPrimaryDiscount &&
        existingPrimaryDiscount && restoredOrigin && restoredOrigin !== 'locally_applied' &&
        this.dataset.discountOrigin !== discountOriginAttributes(existingPrimaryDiscount, restoredOrigin,
          Theme.legacyBox.discounts.autoApply.originalCode || Theme.legacyBox.discounts.autoApply.code)[DISCOUNT_ORIGIN_ATTRIBUTE]) {
      await this.applyDiscount(null, existingPrimaryDiscount, restoredOrigin);
      return;
    }

    // Refresh only after override and code transitions, and only while this snapshot is current.
    if (!this.#activeFetch && existingPrimaryDiscount && storedCode === existingPrimaryDiscount &&
      isDiscountQuoteStale(discountsData) &&
      sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) === storage) {
      const controller = this.#createAbortController();
      try {
        if (await this.#persistPrimaryDiscount(existingPrimaryDiscount,
          restoredOrigin || discountsData.applied || 'locally_applied', controller.signal, storage, true)) {
          this.#notifyDisplayChange();
        }
      } finally {
        if (this.#activeFetch === controller) this.#activeFetch = null;
      }
    }
  }

  /**
   * @param {unknown} code
   * @returns {string | null}
   */
  #normalizeDiscountCode(code) {
    return typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;
  }

  /**
   * @param {string[]} codes
   * @returns {string | null}
   */
  #getPrimaryDiscountCode(codes) {
    for (const code of codes) {
      const normalizedCode = this.#normalizeDiscountCode(code);

      if (normalizedCode && !isAdditiveFreeProcessingDiscountCode(normalizedCode)) {
        return normalizedCode;
      }
    }

    return null;
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountValue() {
    return getPrimaryDiscountCodeFromUrl();
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountCode() {
    return this.#normalizeDiscountCode(this.#getUrlDiscountValue());
  }

  /**
   * @param {any} override
   * @param {any} discountsData
   */
  #isOverrideDiscountActive(override, discountsData) {
    if (override?.additive) return false;
    if (!override?.is_bogo && !override?.require_code) return true;

    const overrideCode = this.#normalizeDiscountCode(override?.auto_discount);

    if (!overrideCode) return false;

    if (override.require_code) {
      return (this.#getUrlDiscountCode() ||
        (discountsData?.status !== 'fail' && this.#normalizeDiscountCode(discountsData?.code))) === overrideCode;
    }

    return (this.#getUrlDiscountCode() || this.#normalizeDiscountCode(discountsData?.code)) === overrideCode;
  }

  async #autoDiscounts() {
    if (!this.isConnected || this.#pageHidden) return;
    const generation = this.#connectionGeneration;
    const storage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);

    if (storage) return;

    const type = this.#determineDiscountApplication();

    if (!Theme.legacyBox.discounts.allow || !type) return;

    switch (type) {
      case 'url_query': {
        const url = new URL(window.location.href);
        const code = this.#getUrlDiscountValue();

        if (code) {
          const requestHandled = await this.applyDiscount(null, code, type);

          if (requestHandled) {
            url.searchParams.delete('lb_discount');
            url.searchParams.delete('discount');
            window.history.replaceState({}, '', url);
          }
        }

        break;
      }
      case 'auto_apply': {
        const revision = this.#discountOperationRevision;
        const selectionUrlKey = () => {
          const url = new URL(window.location.href);
          return JSON.stringify([url.origin, url.pathname, ...['discount', 'lb_discount', 'lb_discount_type']
            .map((key) => url.searchParams.get(key))]);
        };
        const url = selectionUrlKey();
        if (this.#activeFetch) return;
        const code = await this.#resolveAutoApplyCode(
          Theme.legacyBox.discounts.autoApply.code,
          true
        );

        if (!this.isConnected || generation !== this.#connectionGeneration) return;
        // Assignment may finish after a customer has started or completed another selection.
        if (this.#activeFetch || revision !== this.#discountOperationRevision ||
            sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) !== storage ||
            selectionUrlKey() !== url) return;
        const applying = code ? this.applyDiscount(null, code, type) : Promise.resolve(false);
        const applyingRevision = this.#discountOperationRevision;
        const applied = await applying;
        // A superseded primary request must not start an additive fallback either.
        if (!this.isConnected || generation !== this.#connectionGeneration ||
            applyingRevision !== this.#discountOperationRevision || this.#activeFetch ||
            selectionUrlKey() !== url) return;
        const persistedCode = this.#normalizeDiscountCode(
          this.#readSession(Theme.legacyBox.discounts.sessionKey)?.code
        );
        const primaryApplied =
          code && persistedCode === this.#normalizeDiscountCode(code) && applied;

        if (!primaryApplied) {
          const [standaloneAdditiveCode] = buildDiscountCodeList(null);
          if (
            standaloneAdditiveCode &&
            !isAdditiveFreeProcessingDiscountCode(code) &&
            this.#normalizeDiscountCode(standaloneAdditiveCode) !==
              this.#normalizeDiscountCode(code)
          ) {
            await this.applyDiscount(null, standaloneAdditiveCode, 'campaign');
          }
        }

        break;
      }
      default:
        break;
    }
  }

  #determineDiscountApplication() {
    const storage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);

    if (storage) {
      try {
        const data = JSON.parse(storage);

        if (data?.applied) return data.applied;
      } catch (e) {}
    }

    const urlValue = this.#getUrlDiscountValue();
    if (urlValue) return 'url_query';

    if (Theme.legacyBox.discounts.autoApply.active) {
      return 'auto_apply';
    }

    return null;
  }

  /**
   * @param {string} key
   */
  #readSession(key) {
    if (!key) return null;

    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) return null;

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Persists a primary code only after Shopify has confirmed it in the cart. Worker metadata is
   * optional; a minimal record prevents a transient worker failure from restoring a stale code.
   *
   * @param {string} code
   * @param {string} type
   * @param {AbortSignal} [signal]
   * @param {string | null} [expectedSession] - Reject background results after another code change.
   * @param {boolean} [changedOnly] - Report only meaningful display changes on background refresh.
   */
  async #persistPrimaryDiscount(code, type, signal = this.#connectionController.signal, expectedSession, changedOnly = false) {
    const normalizedCode = this.#normalizeDiscountCode(code);
    if (!normalizedCode) return false;
    const sessionSnapshot = expectedSession === undefined
      ? sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) : expectedSession;

    let discountData;
    try {
      discountData = await this.#getDiscountData(normalizedCode, signal);
    } catch (error) {
      if (signal?.aborted) return false;
      console.error(error);
    }

    if (signal?.aborted) return false;
    if (sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey) !== sessionSnapshot) return false;
    const hasWorkerData = discountData?.status === 'success';
    if (!hasWorkerData) {
      discountData = { code: normalizedCode };
    }

    const sessionData = JSON.stringify({
      applied: type,
      ...discountData,
      quoteLookupAt: discountData.quoteLookupAt,
      ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(normalizedCode, type) || {}),
    });

    sessionStorage.setItem(Theme.legacyBox.discounts.sessionKey, sessionData);
    if (type === 'auto_apply') {
      if (hasWorkerData) {
        sessionStorage.setItem(Theme.legacyBox.discounts.defaultSessionKey, sessionData);
      } else {
        this.#clearAutoApplyDefaultDiscount();
      }
    } else if (type !== 'override_applied') {
      this.#clearAutoApplyDefaultDiscount();
    }

    return !changedOnly || discountStateKey(JSON.parse(sessionSnapshot || 'null')) !== discountStateKey(JSON.parse(sessionData));
  }

  #notifyDisplayChange() {
    if (this.isConnected) document.dispatchEvent(new DiscountUpdateEvent({}, this.id, { cartChanged: false }));
  }

  #clearAutoApplyDefaultDiscount() {
    sessionStorage.removeItem(Theme.legacyBox.discounts.defaultSessionKey);
  }

  /**
   * Resolves the sitewide assignment without allowing an A/B service failure to block the theme's
   * configured fallback discount.
   *
   * @param {string | null} fallbackCode
   * @param {boolean} [triggerAssignment]
   */
  async #resolveAutoApplyCode(fallbackCode, triggerAssignment = false) {
    const signal = this.#connectionController.signal;
    if (!Theme.legacyBox.discounts.autoApply.active || !fallbackCode) return null;

    try {
      const code = (
        (await window.Legacybox?.resolveSitewideAutoApplyDiscountCode?.({
          fallbackCode,
          triggerAssignment,
        })) || fallbackCode
      );
      return signal.aborted ? null : code;
    } catch (error) {
      if (signal.aborted) return null;
      console.error(error);
      return fallbackCode;
    }
  }

  /**
   * Keep product-price.js' default_discount fallback aligned with the active
   * auto-apply code. This prevents old sitewide A/B assignments from leaving
   * stale strikethrough pricing in session storage.
   *
   * @param {string | null} autoApplyCode
   * @returns {Promise<boolean>}
   */
  async #syncAutoApplyDefaultDiscount(autoApplyCode) {
    const signal = this.#connectionController.signal;
    if (!Theme.legacyBox.discounts.autoApply.active || !autoApplyCode) {
      this.#clearAutoApplyDefaultDiscount();
      return false;
    }

    const normalizedAutoApplyCode = this.#normalizeDiscountCode(autoApplyCode);

    if (!normalizedAutoApplyCode) {
      this.#clearAutoApplyDefaultDiscount();
      return false;
    }

    const currentDefaultDiscount = this.#readSession(Theme.legacyBox.discounts.defaultSessionKey);
    const currentDefaultCode = this.#normalizeDiscountCode(currentDefaultDiscount?.code);

    if (currentDefaultCode === normalizedAutoApplyCode && !isDiscountQuoteStale(currentDefaultDiscount)) return false;

    if (
      this.#defaultDiscountSyncPromise &&
      this.#defaultDiscountSyncCode === normalizedAutoApplyCode
    ) {
      return this.#defaultDiscountSyncPromise;
    }

    this.#defaultDiscountSyncCode = normalizedAutoApplyCode;
    this.#defaultDiscountSyncPromise = (async () => {
      let defaultDiscountData;
      try {
        defaultDiscountData = await this.#getDiscountData(normalizedAutoApplyCode, signal);
      } catch (error) {
        if (signal.aborted) return false;
        console.error(error);
        this.#clearAutoApplyDefaultDiscount();
        return false;
      }

      if (signal.aborted || defaultDiscountData?.status !== 'success') return false;

      sessionStorage.setItem(
        Theme.legacyBox.discounts.defaultSessionKey,
        JSON.stringify({
          ...defaultDiscountData,
          quoteLookupAt: defaultDiscountData.quoteLookupAt,
          ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(
            normalizedAutoApplyCode,
            'auto_apply'
          ) || {}),
        })
      );

      return true;
    })();

    const pending = this.#defaultDiscountSyncPromise;
    try {
      return await pending;
    } finally {
      if (this.#defaultDiscountSyncPromise === pending) {
        this.#defaultDiscountSyncCode = null;
        this.#defaultDiscountSyncPromise = null;
      }
    }
  }

  #createAbortController() {
    this.#discountOperationRevision++;
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #adoptCartSnapshot(cart) {
    Theme.legacyBox.cart = cart;
    this.#cartDiscountCodes = (cart.discount_codes || [])
      .filter(discount => this.#isDiscountPresent(cart, discount.code)).map(discount => discount.code);
    this.dataset.discountOrigin = cart.attributes?.[DISCOUNT_ORIGIN_ATTRIBUTE] || '';
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent | null} event - The submit event on our form.
   * @param {String} code - Discount code.
   */
  applyDiscount = async (event, code, type = 'locally_applied') => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isConnected || this.#pageHidden) return false;
    let discountCode;
    let discountCodeValue = code ? code : '';
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;

    if (!code) {
      event?.preventDefault();
      event?.stopPropagation();

      const form = event?.target;
      if (!(form instanceof HTMLFormElement)) return;

      discountCode = form.querySelector('input[name="discount"]');
      if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string')
        return;

      discountCodeValue = discountCode.value;
      type = 'manually_applied';
    }

    while (isCartMutating()) await waitForCartMutations();
    if (!this.isConnected || this.#pageHidden) return false;

    const rejectionKey = `${Theme.legacyBox.discounts.sessionKey}:rejected-auto`;
    const attempt = JSON.stringify({
      code: this.#normalizeDiscountCode(discountCodeValue),
      previous: {
        code: this.#normalizeDiscountCode(this.#readSession(Theme.legacyBox.discounts.sessionKey)?.code),
        applied: this.#readSession(Theme.legacyBox.discounts.sessionKey)?.applied,
      },
      items: Theme.legacyBox.cart.items.map((item) => ({
        id: item.id, quantity: item.quantity, properties: item.properties,
        sellingPlan: item.selling_plan_allocation?.selling_plan?.id,
      })),
    });
    if (type === 'auto_apply' && sessionStorage.getItem(rejectionKey) === attempt) return true;

    const endMutation = beginCartMutation();
    const abortController = this.#createAbortController();
    const container = this.closest('.cart-discount');

    try {
      const existingDiscounts = this.#existingDiscounts();
      const existingOrigin = this.dataset.discountOrigin || '';
      const requestedDiscounts = buildDiscountCodeList(discountCodeValue);
      const requestedCodeIsAdditive = isAdditiveFreeProcessingDiscountCode(discountCodeValue);
      const originAttributes = requestedCodeIsAdditive ? {} : discountOriginAttributes(
        discountCodeValue, type, Theme.legacyBox.discounts.autoApply.originalCode || Theme.legacyBox.discounts.autoApply.code
      );
      const normalizedExistingDiscounts = existingDiscounts
        .map((existingCode) => this.#normalizeDiscountCode(existingCode))
        .filter(Boolean);

      if (
        requestedDiscounts.length > 0 &&
        requestedDiscounts.every((requestedCode) =>
          normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(requestedCode))
        ) &&
        !Theme.legacyBox.discounts.overrides.some(
          (o) => !o.additive && !o.campaign_display && o.auto_discount.toLowerCase() === discountCodeValue.toLowerCase()
        )
      ) {
        if (!requestedCodeIsAdditive) {
          if (this.dataset.discountOrigin !== originAttributes[DISCOUNT_ORIGIN_ATTRIBUTE]) {
            // Provenance-only backfill: do not reapply a valid code or redraw the cart.
            const response = await fetch(Theme.routes.cart_update_url, {
              ...fetchConfig('json', { body: JSON.stringify({ attributes: originAttributes }) }),
              signal: abortController.signal,
            });
            if (!response.ok) throw new Error(`Discount origin update failed (${response.status})`);
            const cart = await response.json();
            if (abortController.signal.aborted) return false;
            this.dataset.discountOrigin = cart.attributes?.[DISCOUNT_ORIGIN_ATTRIBUTE] || originAttributes[DISCOUNT_ORIGIN_ATTRIBUTE];
            Theme.legacyBox.cart.attributes = { ...Theme.legacyBox.cart.attributes, ...originAttributes };
          }
          const stored = this.#readSession(Theme.legacyBox.discounts.sessionKey);
          if (this.#normalizeDiscountCode(stored?.code) !== this.#normalizeDiscountCode(discountCodeValue) ||
              stored?.applied !== type || isDiscountQuoteStale(stored)) {
            if (!(await this.#persistPrimaryDiscount(discountCodeValue, type, abortController.signal))) return false;
            this.#notifyDisplayChange();
          }
        }
        return true;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      container?.classList.add('is-loading');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: requestedDiscounts.join(','),
          attributes: originAttributes,
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`Discount update failed (${response.status})`);

      const data = await response.json();
      if (abortController.signal.aborted) return false;
      this.#adoptCartSnapshot(data);

      const normalizedRequestedCode = this.#normalizeDiscountCode(discountCodeValue);
      const hasRequestedLineDiscount = data.items?.some((i) =>
        i.discounts?.some((d) => this.#normalizeDiscountCode(d.title) === normalizedRequestedCode)
      );
      const primaryRejected = data.discount_codes?.some(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          this.#normalizeDiscountCode(discount.code) === normalizedRequestedCode &&
          discount.applicable === false
      );

      if (primaryRejected && !hasRequestedLineDiscount) {
        // Survive section remounts; retry when the code, selection, or cart changes.
        if (type === 'auto_apply') sessionStorage.setItem(rejectionKey, attempt);
        if (requestedCodeIsAdditive) {
          reconcileFreeProcessingDiscount(data, { clearRejected: true });
        }

        {
          // A rejected replacement can still remove the previous code in Shopify.
          // Restore the prior cart for every rejection, not only additive codes.
          const previousPrimary = this.#getPrimaryDiscountCode(existingDiscounts) ||
            this.#normalizeDiscountCode(this.#readSession(Theme.legacyBox.discounts.sessionKey)?.code);
          const rollbackResponse = await fetch(Theme.routes.cart_update_url, {
            ...fetchConfig('json', {
              body: JSON.stringify({
                discount: existingDiscounts.join(','),
                attributes: { [DISCOUNT_ORIGIN_ATTRIBUTE]: existingOrigin },
                sections: [this.dataset.sectionId],
              }),
            }),
            signal: abortController.signal,
          });
          if (!rollbackResponse.ok) {
            reconcileFreeProcessingDiscount(data);
            if (previousPrimary && !this.#isDiscountPresent(data, previousPrimary)) {
              sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
              this.#clearAutoApplyDefaultDiscount();
            }
            document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { renderedSectionId: data.sections?.[this.dataset.sectionId] ? this.dataset.sectionId : null }));

            const currentHtml = data.sections?.[this.dataset.sectionId];
            if (currentHtml) {
              document.body.classList.remove('discounts-override-applying');
              await morphSection(this.dataset.sectionId, currentHtml);
            }

            throw new Error(`Discount rollback failed (${rollbackResponse.status})`);
          }

          const rollbackData = await rollbackResponse.json();
          if (abortController.signal.aborted) return false;
          this.#adoptCartSnapshot(rollbackData);
          reconcileFreeProcessingDiscount(rollbackData);

          if (previousPrimary && !this.#isDiscountPresent(rollbackData, previousPrimary)) {
            sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
            this.#clearAutoApplyDefaultDiscount();
          }

          document.dispatchEvent(new DiscountUpdateEvent(rollbackData, this.id, { renderedSectionId: rollbackData.sections?.[this.dataset.sectionId] ? this.dataset.sectionId : null }));
          const rollbackHtml = rollbackData.sections?.[this.dataset.sectionId];
          if (rollbackHtml) {
            document.body.classList.remove('discounts-override-applying');
            await morphSection(this.dataset.sectionId, rollbackHtml);
          }
        }

        if (discountCode) discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return true;
      }

      sessionStorage.removeItem(rejectionKey);
      reconcileFreeProcessingDiscount(data);

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement ? element.dataset.discountCode : null
          )
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find(
            (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return (
                this.#normalizeDiscountCode(discount.code) === normalizedRequestedCode &&
                discount.applicable === true
              );
            }
          ) &&
          !hasRequestedLineDiscount
        ) {
          this.#handleDiscountError('shipping');
          if (discountCode) discountCode.value = '';
          return true;
        }
      }

      if (!requestedCodeIsAdditive) {
        const config = Theme.legacyBox.discounts;
        const minimal = JSON.stringify({ code: normalizedRequestedCode, applied: type,
          ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(normalizedRequestedCode, type) || {}) });
        sessionStorage.setItem(config.sessionKey, minimal);
        this.#clearAutoApplyDefaultDiscount();
        // Page-owned enrichment survives the section morph but cannot overwrite a newer choice.
        void this.#persistPrimaryDiscount(discountCodeValue, type, null, minimal).then(changed => {
          if (changed) document.dispatchEvent(new DiscountUpdateEvent({}, this.id, { cartChanged: false }));
        });
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { renderedSectionId: data.sections?.[this.dataset.sectionId] ? this.dataset.sectionId : null }));

      // The override loading class hides price text; remove it before replacing
      // the cart DOM so morphed markup does not briefly paint as a placeholder.
      document.body.classList.remove('discounts-override-applying');
      await morphSection(this.dataset.sectionId, newHtml);
      return true;
    } catch (error) {
      console.error(error);
      if (!abortController.signal.aborted) blockCartCheckout();
      return false;
    } finally {
      endMutation();
      if (this.#activeFetch === abortController) {
        container?.classList.remove('is-loading');
        this.#activeFetch = null;
      }

      if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent | null} event - The mouse or keyboard event in our pill.
   * @param {String} code - Discount code
   */
  removeDiscount = async (event, code) => {
    event?.preventDefault();
    event?.stopPropagation();
    while (isCartMutating()) await waitForCartMutations();
    if (!this.isConnected || this.#pageHidden) return;
    let discountCode = code;

    if (event) {
      event.preventDefault();
      event.stopPropagation();

      if (
        (event instanceof KeyboardEvent && event.key !== 'Enter') ||
        !(event instanceof MouseEvent) ||
        !(event.target instanceof HTMLElement) ||
        typeof this.dataset.sectionId !== 'string'
      ) {
        return;
      }

      const pill = event.target.closest('.cart-discount__pill');
      if (!(pill instanceof HTMLLIElement)) return;

      discountCode = pill.dataset.discountCode || '';
    }

    if (!discountCode) return;

    const removingAdditiveFreeProcessingDiscount =
      isAdditiveFreeProcessingDiscountCode(discountCode);

    const existingDiscounts = this.#existingDiscounts();
    const normalizedDiscountCode = this.#normalizeDiscountCode(discountCode);
    const index = existingDiscounts.findIndex(
      (existingCode) => this.#normalizeDiscountCode(existingCode) === normalizedDiscountCode
    );
    if (index === -1) return;

    existingDiscounts.splice(index, 1);
    const remainingDiscounts = removingAdditiveFreeProcessingDiscount
      ? existingDiscounts
      : existingDiscounts.filter((existingCode) => {
          return !isAdditiveFreeProcessingDiscountCode(existingCode);
        });

    const endMutation = beginCartMutation();
    const abortController = this.#createAbortController();
    const container = this.closest('.cart-discount');
    container?.classList.add('is-loading');

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: remainingDiscounts.join(','),
          attributes: removingAdditiveFreeProcessingDiscount ? {} : { [DISCOUNT_ORIGIN_ATTRIBUTE]: '' },
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`Discount removal failed (${response.status})`);

      const data = await response.json();
      if (abortController.signal.aborted) return false;
      this.#adoptCartSnapshot(data);
      const isDiscountStillPresent = this.#isDiscountPresent(data, normalizedDiscountCode);

      if (!isDiscountStillPresent) {
        if (removingAdditiveFreeProcessingDiscount) {
          clearFreeProcessingDiscount();
        } else {
          sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
          this.#clearAutoApplyDefaultDiscount();
        }
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { renderedSectionId: data.sections?.[this.dataset.sectionId] ? this.dataset.sectionId : null }));
      }

      document.body.classList.remove('discounts-override-applying');
      await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      console.error(error);
      if (!abortController.signal.aborted) blockCartCheckout();
    } finally {
      endMutation();
      if (this.#activeFetch === abortController) {
        container?.classList.remove('is-loading');
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;
    const target =
      type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Confirms a code from every discount location returned by Shopify's Cart API.
   *
   * @param {any} cart
   * @param {unknown} code
   */
  #isDiscountPresent(cart, code) {
    const normalizedCode = this.#normalizeDiscountCode(code);
    if (!normalizedCode) return false;

    return Boolean(
      cart?.discount_codes?.some(
        (discount) =>
          this.#normalizeDiscountCode(discount.code) === normalizedCode &&
          discount.applicable !== false
      ) ||
      cart?.cart_level_discount_applications?.some(
        (discount) => this.#normalizeDiscountCode(discount.title) === normalizedCode
      ) ||
      cart?.items?.some((item) =>
        item.discounts?.some(
          (discount) => this.#normalizeDiscountCode(discount.title) === normalizedCode
        )
      )
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    if (this.#cartDiscountCodes) return [...this.#cartDiscountCodes];
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = document.querySelector('cart-discount-component')
      ? this.querySelectorAll('.cart-discount__pill')
      : [];

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Returns discount code information.
   * @param {string} code
   * @param {AbortSignal} [signal]
   */
  async #getDiscountData(code, signal = this.#connectionController.signal) {
    const data = await lookupDiscountMetadata(Theme.legacyBox.discounts, code);
    if (signal?.aborted) return null;

    return data;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
